import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { signOut, type Me } from '../lib/api';
import { useMe } from '../lib/queries';
import { saveTheme, storedTheme, watchSystemTheme, type ThemeChoice } from '../lib/theme';
import { cx, ErrorNote, Loading, Logo } from './ui';

export const isOrgAdmin = (me: Me, orgId?: string) => me.organizations.some((o) => o.role === 'admin' && (!orgId || o.id === orgId));

/** Signed-in area: header, navigation, and the page. */
export function Layout() {
  const me = useMe();
  const location = useLocation();
  useEffect(() => watchSystemTheme(), []);

  if (me.isPending) return <Loading />;
  if (me.error) return <div className="p-6"><ErrorNote error={me.error} /></div>;
  if (!me.data) return <Navigate to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;

  const nav = [
    { to: '/', label: 'Plants', end: true },
    { to: '/alarms', label: 'Alarms' },
    ...(isOrgAdmin(me.data) ? [{ to: '/members', label: 'Members' }, { to: '/api-keys', label: 'API keys' }] : []),
    { to: '/connect', label: 'Connect' },
    ...(me.data.platformRole === 'vardex_admin' ? [{ to: '/admin', label: 'Admin' }] : []),
  ];

  return (
    <div className="min-h-screen">
      <header className="border-b border-rule bg-surface">
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-4 py-2.5">
          <Link to="/" className="flex items-center gap-2" aria-label="Vardex home">
            <Logo />
            <span className="font-display text-lg font-semibold tracking-tight">Vardex</span>
          </Link>
          <nav className="flex flex-1 gap-1 overflow-x-auto [scrollbar-width:none]">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cx('whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm font-medium', isActive ? 'bg-accent-soft text-ink' : 'text-muted hover:text-ink')
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <UserMenu me={me.data} />
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function UserMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(storedTheme());
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-sm font-semibold"
        aria-label="Account"
        aria-expanded={open}
      >
        {(me.user.name || me.user.email).charAt(0).toUpperCase()}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-2 w-64 rounded-lg border border-rule bg-surface p-3 text-sm shadow-lg">
            <div className="font-medium">{me.user.name}</div>
            <div className="truncate text-muted">{me.user.email}</div>
            {me.platformRole && <div className="mt-1 text-xs text-muted">{me.platformRole === 'vardex_admin' ? 'Vardex admin' : 'Vardex support'}</div>}
            <div className="mt-3 border-t border-rule pt-3">
              <div className="mb-1 text-xs text-muted">Theme</div>
              <div className="flex gap-1">
                {(['system', 'light', 'dark'] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      saveTheme(t);
                      setTheme(t);
                    }}
                    className={cx('flex-1 rounded-md px-2 py-1 capitalize', theme === t ? 'bg-accent-soft font-medium' : 'text-muted hover:text-ink')}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <button
              type="button"
              className="mt-3 w-full rounded-md border border-rule px-2 py-1.5 text-left hover:border-muted"
              onClick={async () => {
                await signOut();
                queryClient.clear();
                navigate('/sign-in');
              }}
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
